import { resolveClampForecastExpectedArrivalDate } from "./clamp-forecast-resolve-expected-arrival.js";

export interface ClampForecastPoLineToCreate {
  sku: string;
  quantity: number;
  unit_price?: number;
}

export interface ClampForecastMonthPoCellInput {
  id: number;
  category_code: string;
  plan_month: string;
  forecast_qty: number;
  expected_arrival_date: string | null;
}

export interface ClampForecastMonthPoCategoryInput {
  code: string;
  is_kit: boolean;
  fg_sku: string | null;
  active: boolean;
  sort_order: number;
}

export interface ClampForecastMonthPoKitItemInput {
  component_sku: string;
  quantity_per_kit: number;
}

export interface ClampForecastTaggedPoLine extends ClampForecastPoLineToCreate {
  forecast_id: number;
  expected_delivery_date: string | null;
}

export interface ClampForecastMonthPoSkippedCell {
  forecast_id: number;
  category_code: string;
  reason: string;
}

export interface ClampForecastMonthPoPlan {
  mv_lines: ClampForecastPoLineToCreate[];
  tagged_lines: ClampForecastTaggedPoLine[];
  skipped_cells: ClampForecastMonthPoSkippedCell[];
  included_cell_count: number;
}

/**
 * Build the PO line list for a forecast cell.
 *
 * Non-kit categories emit a single PO line for the finished-good SKU at the
 * forecast quantity. Kit categories explode into one line per kit item.
 */
export interface ClampForecastLinkedPoLineInput {
  sku: string;
  quantity_ordered: number;
}

export interface ClampForecastMonthPoLinkInput extends ClampForecastLinkedPoLineInput {
  forecast_id: number;
}

/**
 * Forecast quantity not yet covered by linked purchase order lines for this cell.
 * Non-kit categories compare against the finished-good SKU; kit categories use the
 * bottleneck component (fewest kit-equivalent units already on POs).
 */
export function computeRemainingClampForecastAllocation(input: {
  forecast_qty: number;
  category: { is_kit: boolean; fg_sku: string | null };
  kitItems: { component_sku: string; quantity_per_kit: number }[];
  linkedLines: ClampForecastLinkedPoLineInput[];
}): number {
  const forecast = Math.max(0, Math.floor(Number(input.forecast_qty)));
  if (forecast === 0) return 0;

  const linkedBySku = new Map<string, number>();
  for (const line of input.linkedLines) {
    const sku = line.sku.trim().toUpperCase();
    if (sku === "") continue;
    linkedBySku.set(sku, (linkedBySku.get(sku) ?? 0) + Number(line.quantity_ordered ?? 0));
  }

  if (!input.category.is_kit) {
    const fgSku = (input.category.fg_sku ?? "").trim().toUpperCase();
    if (fgSku === "") return forecast;
    const linked = linkedBySku.get(fgSku) ?? 0;
    return Math.max(0, forecast - linked);
  }

  const kitItems = input.kitItems.filter((item) => Number(item.quantity_per_kit) > 0);
  if (kitItems.length === 0) return forecast;

  let kitsCovered = forecast;
  for (const item of kitItems) {
    const sku = item.component_sku.trim().toUpperCase();
    const linked = linkedBySku.get(sku) ?? 0;
    const covered = Math.floor(linked / Number(item.quantity_per_kit));
    kitsCovered = Math.min(kitsCovered, covered);
  }
  return Math.max(0, forecast - kitsCovered);
}

/**
 * Quantity to show on the grid's linked-PO line and compare to forecast_qty.
 * Non-kit: linked finished-good units. Kit: kit-equivalent units (bottleneck component), not the sum of component line qtys.
 */
export function computeClampForecastOrderedForDisplay(
  input: Parameters<typeof computeRemainingClampForecastAllocation>[0],
): number {
  const forecast = Math.max(0, Math.floor(Number(input.forecast_qty)));
  if (forecast === 0) return 0;
  const remaining = computeRemainingClampForecastAllocation(input);
  return forecast - remaining;
}

export function buildClampForecastPoLines(input: {
  category: { is_kit: boolean; fg_sku: string | null };
  kitItems: { component_sku: string; quantity_per_kit: number }[];
  forecast_qty: number;
}): ClampForecastPoLineToCreate[] {
  const qty = Math.max(1, Math.floor(input.forecast_qty));
  if (input.category.is_kit) {
    return input.kitItems
      .filter((item) => Number(item.quantity_per_kit) > 0)
      .map((item) => ({
        sku: item.component_sku.trim(),
        quantity: Math.max(1, Math.round(qty * Number(item.quantity_per_kit))),
      }));
  }
  const fg = (input.category.fg_sku ?? "").trim();
  if (fg === "") return [];
  return [{ sku: fg, quantity: qty }];
}

/**
 * Build one Megaventory PO line list plus per-forecast link metadata for every
 * positive forecast cell in a plan month that still needs quantity on purchase orders.
 */
export function buildClampForecastMonthPoPlan(input: {
  plan_month: string;
  cells: ClampForecastMonthPoCellInput[];
  categories: ClampForecastMonthPoCategoryInput[];
  kitItemsByCategory: Map<string, ClampForecastMonthPoKitItemInput[]>;
  poLinks: ClampForecastMonthPoLinkInput[];
}): ClampForecastMonthPoPlan {
  const linkedLinesByForecastId = new Map<number, ClampForecastLinkedPoLineInput[]>();
  for (const link of input.poLinks) {
    const list = linkedLinesByForecastId.get(link.forecast_id) ?? [];
    list.push({ sku: link.sku, quantity_ordered: link.quantity_ordered });
    linkedLinesByForecastId.set(link.forecast_id, list);
  }
  const categoryByCode = new Map(input.categories.map((category) => [category.code, category]));
  const monthCells = input.cells
    .filter((cell) => cell.plan_month === input.plan_month && Number(cell.forecast_qty) > 0)
    .sort((left, right) => {
      const leftCategory = categoryByCode.get(left.category_code);
      const rightCategory = categoryByCode.get(right.category_code);
      const orderDelta = (leftCategory?.sort_order ?? 0) - (rightCategory?.sort_order ?? 0);
      if (orderDelta !== 0) return orderDelta;
      return left.category_code.localeCompare(right.category_code);
    });

  const mv_lines: ClampForecastPoLineToCreate[] = [];
  const tagged_lines: ClampForecastTaggedPoLine[] = [];
  const skipped_cells: ClampForecastMonthPoSkippedCell[] = [];
  let included_cell_count = 0;

  for (const cell of monthCells) {
    const category = categoryByCode.get(cell.category_code);
    if (category == null || !category.active) {
      skipped_cells.push({
        forecast_id: cell.id,
        category_code: cell.category_code,
        reason: "Unknown or inactive category.",
      });
      continue;
    }
    const kitItems = input.kitItemsByCategory.get(cell.category_code) ?? [];
    if (category.is_kit && kitItems.length === 0) {
      skipped_cells.push({
        forecast_id: cell.id,
        category_code: cell.category_code,
        reason: "Kit category has no kit items configured.",
      });
      continue;
    }
    const linkedLines = linkedLinesByForecastId.get(cell.id) ?? [];
    const remainingQty = computeRemainingClampForecastAllocation({
      forecast_qty: Number(cell.forecast_qty),
      category,
      kitItems,
      linkedLines,
    });
    if (remainingQty <= 0) {
      skipped_cells.push({
        forecast_id: cell.id,
        category_code: cell.category_code,
        reason: "Forecast quantity is already fully covered on linked purchase orders.",
      });
      continue;
    }
    const cellLines = buildClampForecastPoLines({
      category,
      kitItems,
      forecast_qty: remainingQty,
    });
    if (cellLines.length === 0) {
      skipped_cells.push({
        forecast_id: cell.id,
        category_code: cell.category_code,
        reason: "Category produced no PO lines.",
      });
      continue;
    }
    included_cell_count += 1;
    const expectedDelivery = resolveClampForecastExpectedArrivalDate(cell);
    for (const line of cellLines) {
      mv_lines.push({ sku: line.sku, quantity: line.quantity });
      tagged_lines.push({
        forecast_id: cell.id,
        sku: line.sku,
        quantity: line.quantity,
        expected_delivery_date: expectedDelivery,
      });
    }
  }

  return { mv_lines, tagged_lines, skipped_cells, included_cell_count };
}
